/* Страница заказа — общий хвост BotRun прототипа (APP-SPEC-4 §8) для React-экранов создания.
   Разметка и классы — как у bot-run.js: .ar / .pr (рамка), герой .ar-hero > .ar-c или .pr-hero > .pr-f с дугами этапов,
   .ar-tx (заголовок и подпись), .ar-more (этапы .tl, результат, действия .ar-acts, «Что дальше» .pr-next, чек .ar-rc),
   подвал .ap-foot с главным действием, которое стоит всегда (пока заказ идёт — неактивным: «Работа ещё создаётся»).
   Ход, ошибки и деньги — только из заказа сервера (order-tail-model.ts); экран задаёт тексты, героя, результат и действия. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useApPad } from '../../../ui/use-ap-pad';
import { uiFormat, uiText } from '../../../i18n';
import { platform } from '../../../platform';
import { OffButton } from '../../../ui/OffButton';
import { ToolIcon } from './ToolIcon';
import { publishStudioOrder, type StudioOrderSnapshot } from '../studio-job-bridge';
import type { CircleFormState } from './CircleFieldInput';
import {
  cNum,
  tailCurrent,
  tailFailText,
  tailPhase,
  tailProgress,
  tailReceipt,
  tailSteps,
  type TailPhase,
  type TailStep,
} from './order-tail-model';
import { markSeen } from './tool-helpers';
import { studioDeliveryOffText, studioDeliveryView } from '../studio-order-tail-model';

export interface TailAction {
  label: string;
  icon?: string;
  onClick(): void | Promise<void>;
  disabled?: boolean;
  /** Неактивная кнопка с причиной (дрожь и подпись, BotCore.offBtn) */
  miss?: string;
}
export interface TailTexts {
  runH: string;
  runP: string;
  doneH: string;
  doneP: ReactNode;
  modH: string;
  /** «кружок не создан», «видео не создано» — в подписи отказа и сбоя */
  notMade: string;
  techH: string;
  modWhy?: string;
  /** Подпись сбоя вместо «Сбой на нашей стороне» — когда причина во входе (в записи нет речи) */
  techWhy?: string;
  /** Подпись строки этапа, не прошедшего проверку */
  modStep?: string;
}
export interface TailHero {
  shape: 'circle' | 'frame';
  /** Ширина к высоте рамки (frame); рамка вписывается в коробку 236 × 300 */
  ratio?: number;
  picture?: ReactNode;
  /** Чем герой сменяется на результате (ролик, плеер, сетка); пусто — кольцо остаётся */
  result?: ReactNode;
  /** Пилюля под кружком на результате (длительность) */
  pill?: string;
}
export interface OrderTailProps {
  state: CircleFormState;
  /** Вид заказа для знака над меню; не задан — экран знак не публикует */
  kind?: StudioOrderSnapshot['kind'];
  hero?: TailHero;
  texts: TailTexts;
  /** Карточка заказа .ar-q и свой результат под текстом */
  card?: ReactNode;
  result?: ReactNode;
  /** Вторые действия итога; не задано — «Скачать» (если есть файл) и «Ещё вариант» */
  acts?: TailAction[];
  next?: TailAction[];
  foot?: { done?: TailAction | null; mod?: TailAction; tech?: TailAction };
  /** Подпись ссылки после сбоя («К настройкам», «К сценарию») */
  backLabel?: string;
  /** Подпись «Изменить …» по отказу проверки (главное действие отказа) */
  modAction?: string;
  /** Подсказка, пока не нажата «Открыть в чате» вне Telegram */
  chatTip?: string;
  /** Индекс файла для «Скачать» */
  downloadIndex?: number;
  /** «Попробовать ещё раз» после сбоя: новый заказ с тем же входом (useTailRetry формы); без него — назад к форме */
  onRetry?(): void;
  className?: string;
}
const BOX = { w: 236, h: 300 };
function frameSize(ratio = 9 / 16) {
  const r = Number.isFinite(ratio) && ratio > 0 ? ratio : 9 / 16;
  return r >= BOX.w / BOX.h ? { w: BOX.w, h: Math.round(BOX.w / r) } : { w: Math.round(BOX.h * r), h: BOX.h };
}
/** Дуги этапов (arcs() прототипа): pathLength 100, зазор gap, текущая заполняется по ориентиру сервера не дальше 90 % (D-256). */
function Arcs({
  shape,
  steps,
  current,
  phase,
  size,
}: {
  shape: 'circle' | 'frame';
  steps: TailStep[];
  current: number;
  phase: TailPhase;
  size: { w: number; h: number };
}) {
  const n = Math.max(1, steps.length);
  const gap = steps.length > 1 ? (shape === 'circle' ? 3.2 : 2.4) : 0;
  const seg = 100 / n - gap;
  const fill = (index: number) => {
    if (!steps.length) return 0;
    const bad = (phase === 'mod' || phase === 'tech') && index === current;
    if (bad || phase === 'done' || index < current || ['done', 'skipped'].includes(steps[index].status)) return seg;
    if (phase === 'run' && index === current) return seg * 0.9;
    return 0;
  };
  const items = Array.from({ length: n }, (_, index) => {
    const offset = -(index * (100 / n) + gap / 2);
    const bad = (phase === 'mod' || phase === 'tech') && index === current && !!steps.length;
    const value = fill(index);
    const now = phase === 'run' && index === current && !!steps.length;
    const style = {
      opacity: value ? undefined : 0,
      strokeDasharray: `${value} ${100 - value}`,
      transition: now && steps[index]?.expectedMs ? `stroke-dasharray ${steps[index].expectedMs}ms linear` : undefined,
    };
    return { offset, bad, style, key: index };
  });
  if (shape === 'circle')
    return (
      <svg className="ar-ring" viewBox="0 0 100 100" aria-hidden="true">
        {items.map(item => (
          <g key={item.key}>
            <circle className="tr" cx="50" cy="50" r="47.5" pathLength={100} strokeDasharray={`${seg} ${100 - seg}`} strokeDashoffset={item.offset} />
            <circle className={`fl${item.bad ? ' bad' : ''}`} cx="50" cy="50" r="47.5" pathLength={100} strokeDashoffset={item.offset} style={item.style} />
          </g>
        ))}
        <circle className="bg" cx="50" cy="50" r="47.5" />
      </svg>
    );
  const pad = 7,
    W = size.w + pad * 2,
    H = size.h + pad * 2,
    R = 26 + pad - 2;
  return (
    <svg className="pr-ring" viewBox={`0 0 ${W} ${H}`} style={{ width: W, height: H, left: -pad, top: -pad }} aria-hidden="true">
      {items.map(item => (
        <g key={item.key}>
          <rect className="tr" x="2" y="2" width={W - 4} height={H - 4} rx={R} pathLength={100} strokeDasharray={`${seg} ${100 - seg}`} strokeDashoffset={item.offset} />
          <rect className={`fl${item.bad ? ' bad' : ''}`} x="2" y="2" width={W - 4} height={H - 4} rx={R} pathLength={100} strokeDashoffset={item.offset} style={item.style} />
        </g>
      ))}
      <rect className="bg" x="2" y="2" width={W - 4} height={H - 4} rx={R} />
    </svg>
  );
}
function Mid({ phase }: { phase: TailPhase }) {
  if (phase === 'mod' || phase === 'tech')
    return (
      <span className="er">
        <ToolIcon kind={phase === 'mod' ? 'shield' : 'warn'} />
      </span>
    );
  return null;
}
/** Скачивание файла результата через авторизованный openResult; ссылка создаётся только после ответа. */
function useTailDownload(state: CircleFormState) {
  const wanted = useRef<number | null>(null);
  const [requested, setRequested] = useState(0);
  useEffect(() => {
    if (wanted.current === null || !state.result) return;
    wanted.current = null;
    const anchor = document.createElement('a');
    anchor.href = state.result.url;
    anchor.download = state.result.name;
    anchor.rel = 'noopener';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  }, [state.result, requested]);
  const available = !!state.order?.result?.available && !!state.order.result.assets.length;
  return {
    available,
    download(index = state.order?.result?.assets[0]?.index ?? 0) {
      if (!available) return;
      wanted.current = index;
      setRequested(value => value + 1);
      if (state.result) return;
      void state.openResult(index);
    },
  };
}
export function OrderTail(props: OrderTailProps) {
  const { state, texts, hero } = props;
  const { order, orderId, busy, submissionBlocked } = state;
  const phase = tailPhase(order);
  const steps = tailSteps(order);
  const current = tailCurrent(order, steps);
  const progress = tailProgress(order);
  const receipt = tailReceipt(order);
  const download = useTailDownload(state);
  const [tip, setTip] = useState('');
  const latest = useRef<StudioOrderSnapshot | null>(null);
  const footRef = useRef<HTMLDivElement>(null);
  useApPad(footRef);
  const owner = state.intent?.owner;
  const appId = state.form?.app_id ?? state.intent?.body?.app_id;
  const status = order?.status;
  const kind = props.kind;
  useEffect(() => {
    if (!kind || !owner || !appId || !orderId || !status) {
      latest.current = null;
      return;
    }
    const snapshot: StudioOrderSnapshot = { owner, appId, orderId, status, kind, shown: true };
    latest.current = snapshot;
    publishStudioOrder(snapshot);
  }, [kind, owner, appId, orderId, status]);
  useEffect(() => {
    const clear = (event: Event) => {
      if ((event as CustomEvent).detail?.id === latest.current?.orderId) latest.current = null;
    };
    document.addEventListener('steos:studio:order-clear', clear);
    return () => {
      document.removeEventListener('steos:studio:order-clear', clear);
      if (latest.current) publishStudioOrder({ ...latest.current, shown: false });
    };
  }, []);
  if (!orderId) return null;
  const leave = async () => {
    const id = orderId;
    if (await state.returnToForm()) {
      markSeen(id);
      document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id } }));
      return true;
    }
    return false;
  };
  const off = busy || submissionBlocked;
  const shape = hero?.shape ?? 'frame';
  const size = frameSize(hero?.ratio);
  const resultHero = phase === 'done' && hero?.result;
  const failed = phase === 'mod' || phase === 'tech';
  const step = steps[current];
  const heading =
    phase === 'done'
      ? texts.doneH
      : phase === 'mod'
        ? texts.modH
        : phase === 'tech'
          ? texts.techH
          : phase === 'uncertain'
            ? 'Уточняем результат'
            : texts.runH;
  // Бот сам не присылает (настройки, Q-221): подпись итога не обещает чат
  const deliveryView = phase === 'done' ? studioDeliveryView(order) : 'pending';
  const deliveryOff = studioDeliveryOffText(deliveryView);
  const sub =
    phase === 'done'
      ? (deliveryOff ?? texts.doneP)
      : failed
        ? tailFailText(phase, order, texts, phase === 'tech' ? step : undefined)
        : phase === 'uncertain'
          ? uiText('Результат пока не подтверждён. Проверьте этот заказ, прежде чем создавать новый.')
          : texts.runP;
  const pill = phase === 'run' && steps.length ? `${current + 1} / ${steps.length}` : phase === 'done' ? hero?.pill : undefined;
  const acts: TailAction[] =
    props.acts ??
    [
      ...(download.available ? [{ label: 'Скачать', icon: 'download', onClick: () => download.download(props.downloadIndex) }] : []),
      { label: 'Ещё вариант', icon: 'refresh', onClick: () => void leave() },
    ];
  const chat: TailAction = {
    label: 'Открыть в чате',
    icon: 'send',
    onClick: () => {
      markSeen(orderId);
      if (platform.kind === 'telegram') platform.close();
      else setTip(deliveryOff ?? props.chatTip ?? 'Бот уже прислал результат в чат с STEOS, в отдельную тему.');
    },
  };
  const doneFoot = props.foot?.done === undefined ? chat : props.foot.done;
  const foot =
    phase === 'done'
      ? doneFoot
      : phase === 'mod'
        ? (props.foot?.mod ?? { label: props.modAction ?? 'Изменить данные', icon: 'edit', onClick: () => void leave() })
        : phase === 'tech'
          ? (props.foot?.tech ?? {
              label: 'Попробовать ещё раз',
              icon: 'refresh',
              onClick: async () => {
                if (props.onRetry) props.onRetry();
                else await leave();
              },
            })
          : null;
  const runLabel = doneFoot?.label;
  return (
    <section className={`order-tail${props.className ? ' ' + props.className : ''}`} aria-live="polite">
      <div className={`${shape === 'circle' ? 'ar' : 'ar pr'} ${phase === 'mod' ? 'fail mod' : phase === 'tech' ? 'fail tech' : phase === 'uncertain' ? 'run' : phase}`}>
        {hero &&
          (resultHero ? (
            <div className="ar-top">{hero.result}</div>
          ) : shape === 'circle' ? (
            <div className="ar-hero">
              <span className="ar-c" aria-hidden="true">
                <span className="ar-pic">
                  <i>{hero.picture}</i>
                </span>
                <Arcs shape="circle" steps={steps} current={current} phase={phase} size={size} />
                <span className="ar-mid">
                  <Mid phase={phase} />
                </span>
                {pill && <span className="ar-pill">{pill}</span>}
              </span>
            </div>
          ) : (
            <div className="pr-hero">
              <div className="pr-f" style={{ width: size.w, height: size.h }} aria-hidden="true">
                <span className="pr-pic">
                  <i>{hero.picture}</i>
                </span>
                <Arcs shape="frame" steps={steps} current={current} phase={phase} size={size} />
                <span className="ar-mid">
                  <Mid phase={phase} />
                </span>
                {pill && <span className="ar-pill pr-pill">{pill}</span>}
              </div>
            </div>
          ))}
        <div className="ar-tx">
          <h2>{uiText(heading)}</h2>
          <p>{typeof sub === 'string' ? uiText(sub) : sub}</p>
          {deliveryView === 'settings' && (
            <button type="button" className="ar-lnk" onClick={() => (window.location.hash = '#settings')}>
              {uiText('Настройки')}
            </button>
          )}
          {phase === 'done' && order?.status === 'done' && !order.result?.available && (
            <p>{uiText('Файл результата пока недоступен. Проверьте работу в «Истории».')}</p>
          )}
        </div>
        <div className="ar-more">
          {(phase === 'run' || phase === 'uncertain' || failed) && !!steps.length && (
            <div className="tl">
              {steps.map((item, index) => {
                const bad = failed && index === current;
                const done = !bad && (index < current || item.status === 'done' || item.status === 'skipped');
                const now = phase === 'run' && index === current && !done;
                return (
                  <div className={`tli${done ? ' done' : ''}${now ? ' now' : ''}${bad ? ' bad' : ''}`} key={item.key}>
                    <span className="ic">
                      <ToolIcon kind={item.icon} />
                      <span className="spn" />
                      <span className="ck">
                        <ToolIcon kind="check" />
                      </span>
                    </span>
                    <div className="t">
                      <span>
                        {uiText(
                          bad
                            ? phase === 'mod'
                              ? (texts.modStep ?? 'Не прошло проверку')
                              : 'Сбой на этом шаге'
                            : done
                              ? item.titleDone
                              : item.title,
                        )}
                      </span>
                      <small>{now && progress ? progress.note || uiFormat('{0} из {1}', progress.done, progress.total) : ''}</small>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {phase === 'uncertain' && (
            <button type="button" className="ar-lnk" disabled={off} onClick={() => void state.refreshStatus()}>
              <ToolIcon kind="refresh" />
              <span>{uiText('Проверить статус')}</span>
            </button>
          )}
          {phase === 'done' && (
            <>
              {props.card}
              {props.result}
              {!!acts.length && (
                <div className="ar-acts">
                  {acts.map(action =>
                    action.miss ? (
                      <OffButton key={action.label} label={uiText(action.label)} miss={uiText(action.miss)} />
                    ) : (
                      <button
                        className="btn"
                        type="button"
                        key={action.label}
                        disabled={off || action.disabled}
                        onClick={() => void action.onClick()}
                      >
                        {action.icon && <ToolIcon kind={action.icon} />}
                        <span>{uiText(action.label)}</span>
                      </button>
                    ),
                  )}
                </div>
              )}
              {!!props.next?.length && (
                <div className="pr-next">
                  <span className="lb">{uiText('Что дальше')}</span>
                  <div className="r">
                    {props.next.map(action => (
                      <button
                        type="button"
                        key={action.label}
                        disabled={off || action.disabled}
                        onClick={async () => {
                          markSeen(orderId);
                          await action.onClick();
                        }}
                      >
                        {action.icon && <ToolIcon kind={action.icon} />}
                        {uiText(action.label)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {phase === 'tech' && (
            <button type="button" className="ar-lnk" disabled={off} onClick={() => void leave()}>
              <ToolIcon kind="back" />
              <span>{uiText(props.backLabel ?? 'К настройкам')}</span>
            </button>
          )}
          {(phase === 'done' || failed) && receipt && (
            <p className="ar-rc">
              {receipt.kind === 'free' ? (
                uiText('Тестовый заказ без списания')
              ) : (
                <>
                  {uiText(receipt.kind === 'refund' ? 'Вернули' : 'Списано')}
                  {receipt.amount !== null && (
                    <>
                      {' '}
                      <b className="mono">{cNum(receipt.amount)} C</b>
                    </>
                  )}
                  {receipt.balance !== null && (
                    <>
                      {' · '}
                      {uiText('остаток')} <b className="mono">{cNum(receipt.balance)} C</b>
                    </>
                  )}
                </>
              )}
            </p>
          )}
          {tip && (
            <p className="hintp order-tail-tip" role="status">
              {uiText(tip)}
            </p>
          )}
        </div>
      </div>
      <div ref={footRef} className="ap-foot">
        {phase === 'run' || phase === 'uncertain' ? (
          runLabel ? (
            <OffButton label={uiText(runLabel)} miss={uiText(phase === 'uncertain' ? 'Проверяем результат' : 'Работа ещё создаётся')} />
          ) : null
        ) : foot ? (
          foot.miss ? (
            <OffButton label={uiText(foot.label)} miss={uiText(foot.miss)} />
          ) : (
            <button className="btn fin" type="button" disabled={off || foot.disabled} onClick={() => void foot.onClick()}>
              {foot.icon && <ToolIcon kind={foot.icon} />}
              <span>{uiText(foot.label)}</span>
            </button>
          )
        ) : null}
      </div>
    </section>
  );
}
